import { useEffect, useRef, useState } from "react";
import { getDestinationCards } from "../services/api";
import type { ICardPaymentMethod } from "../interfaces/payment-method";
import { useStore } from "../store/globalState";

/**
 * Module-level cache for destination (push-to-card) instruments. Warm on the
 * transfer page so the payout-method popup can render the list instantly.
 */
let cache: { linkId: string; data: ICardPaymentMethod[] } | null = null;
let inflight: {
  linkId: string;
  promise: Promise<ICardPaymentMethod[]>;
} | null = null;

const fetchDestinationCards = (
  linkId: string,
  force = false
): Promise<ICardPaymentMethod[]> => {
  if (!force && cache && cache.linkId === linkId) {
    return Promise.resolve(cache.data);
  }
  if (!force && inflight && inflight.linkId === linkId) {
    return inflight.promise;
  }

  const promise = getDestinationCards(linkId)
    .then((data) => {
      cache = { linkId, data };
      if (inflight?.linkId === linkId) inflight = null;
      return data;
    })
    .catch((err) => {
      if (inflight?.linkId === linkId) inflight = null;
      throw err;
    });

  inflight = { linkId, promise };
  return promise;
};

export interface UsePaymentMethodsResult {
  /** Enabled destination cards only. */
  cards: ICardPaymentMethod[];
  /** True only on a cold load (no cached data to show yet). */
  loading: boolean;
}

/**
 * Shared, cached access to destination cards for the payout picker.
 * Re-fetches whenever `cardList` changes after the add-card flow.
 */
export const usePaymentMethods = (
  linkId: string | null | undefined
): UsePaymentMethodsResult => {
  const { cardList } = useStore();

  const hasCache = !!(linkId && cache && cache.linkId === linkId);
  const [cards, setCards] = useState<ICardPaymentMethod[]>(
    hasCache ? cache!.data : []
  );
  const [loading, setLoading] = useState(!hasCache);
  const cardListRef = useRef(cardList);

  useEffect(() => {
    if (!linkId) return;

    const cardListChanged = cardListRef.current !== cardList;
    cardListRef.current = cardList;

    const cached = cache && cache.linkId === linkId ? cache.data : null;

    if (cached) {
      setCards((prev) => (prev === cached ? prev : cached));
      setLoading((prev) => (prev === false ? prev : false));
      if (!cardListChanged) return;
    } else {
      setLoading((prev) => (prev === true ? prev : true));
    }

    let active = true;
    fetchDestinationCards(linkId, cardListChanged)
      .then((res) => {
        if (!active) return;
        setCards(res);
        setLoading(false);
      })
      .catch(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [linkId, cardList]);

  return {
    cards,
    loading,
  };
};
